/**
 * useStaff — detects whether the current user is an IT-SCE staff member.
 *
 * Source of truth ist die Org-Mitgliedschaft: User ist „staff" wenn er
 * Mitglied der `itsce`-Org ist. Backend gated `cost_rate` und interne
 * Pages exakt mit derselben Bedingung (subscriptions.go).
 *
 * Returns `loading=true` während Org-Liste geladen wird. Pages die isStaff
 * gaten sollten während loading einen Placeholder rendern statt zu flackern.
 *
 * Portiert aus customer-portal/src/hooks/useStaff.ts für Unified-Cockpit
 * Bundle A1 — damit Layout.tsx die `staff`-Modus-Sektion korrekt schalten kann.
 */

import { useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api } from '../services/api'
import { isTenantInstance } from '../lib/runtimeConfig'

const ITSCE_ORG_SLUG = 'itsce'

interface StaffState {
  loading: boolean
  /** Mitglied der IT-SCE org (any role) → Cockpit staff-Sektion sichtbar */
  isStaff: boolean
  /** Admin/owner der IT-SCE org → mutierende staff-Aktionen */
  isStaffAdmin: boolean
}

interface MembershipMaybe {
  slug?: string
  role?: string
  member_role?: string
}

export function useStaff(): StaffState {
  const { authenticated, token } = useAuth()
  const [state, setState] = useState<StaffState>({
    loading: true,
    isStaff: false,
    isStaffAdmin: false,
  })

  useEffect(() => {
    // Kunden-Instanz (TQ #409): keine IT-SCE-Org-Mitgliedschaft, cockpit-api hat /api/organizations/my nicht.
    if (!authenticated || !token || isTenantInstance()) {
      setState({ loading: false, isStaff: false, isStaffAdmin: false })
      return
    }

    let cancelled = false
    api
      .getMyOrganizations()
      .then((orgs: MembershipMaybe[]) => {
        if (cancelled) return
        const itsce = orgs.find(o => o.slug === ITSCE_ORG_SLUG)
        const role = itsce?.role ?? itsce?.member_role
        const isStaff = !!itsce
        const isStaffAdmin = isStaff && (role === 'admin' || role === 'owner')
        setState({ loading: false, isStaff, isStaffAdmin })
      })
      .catch((err: unknown) => {
        console.warn('[useStaff] Failed to load organizations:', err)
        if (!cancelled) {
          setState({ loading: false, isStaff: false, isStaffAdmin: false })
        }
      })

    return () => {
      cancelled = true
    }
  }, [authenticated, token])

  return state
}
